import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type Header,
} from '@tanstack/react-table';
import type { LucideIcon } from 'lucide-react';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import {
  ROW_HOVER_BG,
  ROW_SELECTED_BG,
  ROW_SELECTED_MARK_CLASS,
  STICKY_COL_DEFAULT_BG,
  STICKY_COL_HEADER_BG,
  STICKY_COL_SELECTED_BG,
  TABLE_HEADER_BG,
  TABLE_HEADER_TEXT_CLASS,
} from '@/styles/dataTableBg';
import {
  ACTIONS_COLUMN_ID,
  BODY_CELL_PADDING_CLASS,
  BODY_ROW_CLASS,
  STICKY_TABLE_HEADER_CLASS,
} from './dataTable.constants';
import { DataTableLoading } from './DataTableLoading';
import { DataTableEmpty } from './DataTableEmpty';
import { InfiniteLoadingRow } from './InfiniteLoadingRow';
import { SortableColumnHeader } from './SortableColumnHeader';
import { TableScrollFrame } from './TableScrollFrame';
import {
  ARIA_SORT_BY_DIRECTION,
  getNextTableSort,
  type SortDirection,
  type TableSort,
} from './tableSort';

interface DataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  isLoading?: boolean;
  stickyFirstColumn?: boolean;
  stickyColumnId?: string | undefined;
  fitContentColumnIds?: readonly string[] | undefined;
  skeletonRowCount?: number;
  className?: string;
  emptyIcon?: LucideIcon;
  emptyMessage?: string;
  emptyHint?: string | undefined;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  loadMoreError?: boolean;
  getRowClassName?: (row: TData) => string | undefined;
  isRowHighlighted?: ((row: TData) => boolean) | undefined;
  onRowClick?: (row: TData) => void;
  groupFirstColumnBy?: ((row: TData) => unknown) | undefined;
  sort?: TableSort | undefined;
  onSortChange?: ((sort: TableSort | undefined) => void) | undefined;
  sortableColumnIds?: readonly string[] | undefined;
}

const SKELETON_ROW_DEFAULT = 15;
const STICKY_COLUMN_CLASS = 'lg:sticky lg:left-0';
const STICKY_COLUMN_HEADER_CLASS = cn(STICKY_COLUMN_CLASS, STICKY_COL_HEADER_BG);
const FIT_CONTENT_COLUMN_CLASS = 'w-px';
const GROUPED_CELL_CLASS = 'cursor-default border-r border-border/60 bg-card align-top';
const GROUPED_CELL_LABEL_CLASS = 'sticky top-14';

interface RowGroups {
  rowSpans: number[];
  groupStartIndexes: number[];
}

const NO_ROW_GROUPS: RowGroups = { rowSpans: [], groupStartIndexes: [] };

function getRowBgClass(isSelected: boolean): string {
  return isSelected ? cn(ROW_SELECTED_BG, ROW_SELECTED_MARK_CLASS) : ROW_HOVER_BG;
}

function getStickyCellBgClass(isSelected: boolean): string {
  return isSelected ? STICKY_COL_SELECTED_BG : STICKY_COL_DEFAULT_BG;
}

function renderHeaderContent<TData>(header: Header<TData, unknown>) {
  if (header.isPlaceholder) return null;
  return flexRender(header.column.columnDef.header, header.getContext());
}

function groupConsecutiveRows<TData>(
  items: TData[],
  getGroupKey: (item: TData) => unknown,
): RowGroups {
  const rowSpans: number[] = [];
  const groupStartIndexes: number[] = [];
  let groupStartIndex = 0;
  let groupKey: unknown;
  items.forEach((item, index) => {
    const itemGroupKey = getGroupKey(item);
    if (index === 0 || itemGroupKey !== groupKey) {
      groupStartIndex = index;
      groupKey = itemGroupKey;
    }
    rowSpans[index] = 0;
    rowSpans[groupStartIndex] = (rowSpans[groupStartIndex] ?? 0) + 1;
    groupStartIndexes[index] = groupStartIndex;
  });
  return { rowSpans, groupStartIndexes };
}

export function DataTable<TData>({
  data,
  columns,
  isLoading,
  stickyFirstColumn,
  stickyColumnId,
  fitContentColumnIds,
  skeletonRowCount = SKELETON_ROW_DEFAULT,
  className,
  emptyIcon,
  emptyMessage,
  emptyHint,
  onLoadMore,
  hasMore,
  isLoadingMore,
  loadMoreError,
  getRowClassName,
  isRowHighlighted,
  onRowClick,
  groupFirstColumnBy,
  sort,
  onSortChange,
  sortableColumnIds,
}: DataTableProps<TData>) {
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const hasRevealedHighlightedRowRef = useRef(false);
  const revealHighlightedRow = useCallback((row: HTMLTableRowElement | null) => {
    if (!row || hasRevealedHighlightedRowRef.current) return;
    hasRevealedHighlightedRowRef.current = true;
    row.scrollIntoView({ block: 'center' });
  }, []);
  const sentinelRef = useInfiniteScroll<HTMLTableRowElement>({
    onLoadMore,
    hasMore: hasMore && !loadMoreError,
    isLoadingMore,
  });

  useEffect(() => {
    setSelectedRowId(null);
  }, [data]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  function isStickyColumn(columnId: string, index: number): boolean {
    if (stickyColumnId !== undefined) return columnId === stickyColumnId;
    return Boolean(stickyFirstColumn) && index === 0;
  }

  function isFitContentColumn(columnId: string): boolean {
    return columnId === ACTIONS_COLUMN_ID || (fitContentColumnIds?.includes(columnId) ?? false);
  }

  function getSortDirection(columnId: string): SortDirection | undefined {
    return sort?.columnId === columnId ? sort.direction : undefined;
  }

  function renderHeaderCell(header: Header<TData, unknown>) {
    const content = renderHeaderContent(header);
    const columnId = header.column.id;
    if (!onSortChange || !sortableColumnIds?.includes(columnId)) return content;
    return (
      <SortableColumnHeader
        label={content}
        direction={getSortDirection(columnId)}
        onSort={() => onSortChange(getNextTableSort(sort, columnId))}
      />
    );
  }

  function getAriaSort(columnId: string) {
    if (!onSortChange || !sortableColumnIds?.includes(columnId)) return undefined;
    const direction = getSortDirection(columnId);
    return direction ? ARIA_SORT_BY_DIRECTION[direction] : 'none';
  }

  const rows = table.getRowModel().rows;
  const hasRows = rows.length > 0;
  const isFirstColumnGrouped = groupFirstColumnBy !== undefined;
  const rowGroups = useMemo(
    () => (groupFirstColumnBy ? groupConsecutiveRows(data, groupFirstColumnBy) : NO_ROW_GROUPS),
    [data, groupFirstColumnBy],
  );
  const selectedGroupStarts = new Set<number | undefined>();
  if (isFirstColumnGrouped) {
    for (const row of rows) {
      const isHighlighted = isRowHighlighted?.(row.original) ?? false;
      if (isHighlighted || row.id === selectedRowId) {
        selectedGroupStarts.add(rowGroups.groupStartIndexes[row.index]);
      }
    }
  }

  function handleRowClick(rowId: string) {
    setSelectedRowId(rowId);
  }

  return (
    <TableScrollFrame className={className}>
      <table
        className={cn('min-w-full caption-bottom text-sm', !isLoading && !hasRows && 'h-full')}
      >
        <TableHeader className={STICKY_TABLE_HEADER_CLASS}>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="hover:bg-transparent">
              {headerGroup.headers.map((header, index) => (
                <TableHead
                  key={header.id}
                  aria-sort={getAriaSort(header.column.id)}
                  className={cn(
                    'h-9 px-4 text-xs-plus font-semibold whitespace-nowrap',
                    TABLE_HEADER_BG,
                    TABLE_HEADER_TEXT_CLASS,
                    isStickyColumn(header.column.id, index) &&
                      cn(STICKY_COLUMN_HEADER_CLASS, 'z-30'),
                    isFitContentColumn(header.column.id) && FIT_CONTENT_COLUMN_CLASS,
                  )}
                >
                  {renderHeaderCell(header)}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <DataTableLoading
              columnCount={columns.length}
              rowCount={skeletonRowCount}
              columnIds={table.getVisibleLeafColumns().map((column) => column.id)}
            />
          ) : hasRows ? (
            <>
              {rows.map((row) => {
                const isHighlighted = isRowHighlighted?.(row.original) ?? false;
                const isSelected = selectedRowId === row.id || isHighlighted;
                const stickyCellClass = cn(STICKY_COLUMN_CLASS, getStickyCellBgClass(isSelected));
                return (
                  <TableRow
                    key={row.id}
                    ref={isHighlighted ? revealHighlightedRow : undefined}
                    onClick={() => {
                      handleRowClick(row.id);
                      onRowClick?.(row.original);
                    }}
                    className={cn(
                      'group/row',
                      BODY_ROW_CLASS,
                      onRowClick && 'cursor-pointer',
                      getRowBgClass(isSelected),
                      getRowClassName?.(row.original),
                    )}
                  >
                    {row.getVisibleCells().map((cell, index) => {
                      const isGroupedCell = isFirstColumnGrouped && index === 0;
                      const rowSpan = isGroupedCell ? rowGroups.rowSpans[row.index] : undefined;
                      if (rowSpan === 0) return null;

                      const content = flexRender(cell.column.columnDef.cell, cell.getContext());
                      return (
                        <TableCell
                          key={cell.id}
                          rowSpan={rowSpan}
                          onClick={isGroupedCell ? (event) => event.stopPropagation() : undefined}
                          className={cn(
                            BODY_CELL_PADDING_CLASS,
                            'whitespace-nowrap transition-colors',
                            isStickyColumn(cell.column.id, index) && cn(stickyCellClass, 'z-10'),
                            isFitContentColumn(cell.column.id) && FIT_CONTENT_COLUMN_CLASS,
                            isGroupedCell && GROUPED_CELL_CLASS,
                            isGroupedCell && selectedGroupStarts.has(row.index) && ROW_SELECTED_BG,
                          )}
                        >
                          {isGroupedCell ? (
                            <div className={GROUPED_CELL_LABEL_CLASS}>{content}</div>
                          ) : (
                            content
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
              {onLoadMore && (hasMore || loadMoreError) ? (
                <InfiniteLoadingRow
                  ref={sentinelRef}
                  columnCount={columns.length}
                  isLoading={isLoadingMore}
                  hasError={loadMoreError}
                  onRetry={onLoadMore}
                />
              ) : null}
            </>
          ) : (
            <DataTableEmpty
              columnCount={columns.length}
              icon={emptyIcon}
              message={emptyMessage}
              hint={emptyHint}
            />
          )}
        </TableBody>
      </table>
    </TableScrollFrame>
  );
}
